import { useState, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "../utils/cx.js";

export interface TitleProps extends HTMLAttributes<HTMLHeadingElement> {
  level?: 1 | 2 | 3 | 4 | 5;
  children: ReactNode;
}

const TITLE_SIZE: Record<NonNullable<TitleProps["level"]>, string> = {
  1: "text-3xl",
  2: "text-2xl",
  3: "text-xl",
  4: "text-lg",
  5: "text-base",
};

export function Title({ level = 2, className, children, ...rest }: TitleProps) {
  const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4" | "h5";
  return (
    <Tag
      className={cn("scroll-m-20 font-semibold tracking-tight text-foreground", TITLE_SIZE[level], className)}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export type Copyable = boolean | { text?: string; onCopy?: () => void };
export type Ellipsis = boolean | { rows?: number };

export interface TextProps extends HTMLAttributes<HTMLSpanElement> {
  secondary?: boolean;
  small?: boolean;
  strong?: boolean;
  /** Render as inline monospace. */
  code?: boolean;
  keyboard?: boolean;
  /** `true`, or a config (e.g. `{ text }` to copy a value other than the shown one). */
  copyable?: Copyable;
  ellipsis?: Ellipsis;
}

export function Text({
  secondary,
  small,
  strong,
  code,
  keyboard,
  copyable,
  ellipsis,
  className,
  style,
  children,
  ...rest
}: TextProps) {
  const [copied, setCopied] = useState(false);

  const ellipsisStyle: CSSProperties | undefined =
    ellipsis && typeof ellipsis === "object" && ellipsis.rows
      ? {
          display: "-webkit-box",
          WebkitLineClamp: ellipsis.rows,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }
      : undefined;

  const cls = cn(
    secondary && "text-muted-foreground",
    small && "text-xs",
    strong && "font-semibold",
    code && "rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em]",
    keyboard &&
      "rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[0.85em] shadow-[inset_0_-1px_0_var(--border)]",
    ellipsis === true && "inline-block max-w-full truncate align-bottom",
    className,
  );

  const onCopy = () => {
    const text =
      typeof copyable === "object" && copyable.text != null ? copyable.text : String(children ?? "");
    void navigator.clipboard?.writeText(text);
    if (typeof copyable === "object") copyable.onCopy?.();
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <span className={cls} style={ellipsisStyle ? { ...ellipsisStyle, ...style } : style} {...rest}>
      {children}
      {copyable && (
        <button
          type="button"
          onClick={onCopy}
          className="ml-1 inline-flex align-middle text-muted-foreground hover:text-foreground"
          aria-label="Kopyala"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      )}
    </span>
  );
}
